import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";

const LeadMagnetModal = ({ isOpen, onClose, onSave, editData = null }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uuid, setUuid] = useState("");
  const [contentUrl, setContentUrl] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formSubtitleLine1, setFormSubtitleLine1] = useState("");
  const [formSubtitleLine2, setFormSubtitleLine2] = useState("");
  const [ctaButton1Link, setCtaButton1Link] = useState("");
  const [ctaButton2Link, setCtaButton2Link] = useState("");
  const [ctaButton1Label, setCtaButton1Label] = useState("");
  const [ctaButton2Label, setCtaButton2Label] = useState("");
  const [contentSource, setContentSource] = useState("url");
  const [contentFile, setContentFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formationsOffertes, setFormationsOffertes] = useState([]);
  const [showFormationsSection, setShowFormationsSection] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setTitle(editData.title || "");
      setDescription(editData.description || "");
      setUuid(editData.uuid || "");
      setContentUrl(editData.contentUrl || "");
      setFormTitle(editData.formTitle || "");
      setFormSubtitleLine1(editData.formSubtitleLine1 || "");
      setFormSubtitleLine2(editData.formSubtitleLine2 || "");
      setCtaButton1Link(editData.ctaButton1Link || "");
      setCtaButton2Link(editData.ctaButton2Link || "");
      setCtaButton1Label(editData.ctaButton1Label || "");
      setCtaButton2Label(editData.ctaButton2Label || "");
      // setContentSource(editData.thumbnailUrl ? "upload" : "url");
       setContentSource(editData.contentSource || (editData.contentUrl ? "url" : "upload"));
      setContentFile(null);
      setThumbnailFile(null);
      // Ensure formations and downloads have IDs when loading editData
      const formationsWithIds = (editData.formationsOffertes || []).map((formation, idx) => ({
        ...formation,
        id: formation.id || `formation-${idx}`,
        downloads: (formation.downloads || []).map((download, dIdx) => ({
          ...download,
          id: download.id || `download-${idx}-${dIdx}`,
        })),
      }));
      setFormationsOffertes(formationsWithIds);
      setShowFormationsSection(formationsWithIds.length > 0);
    } else {
      setTitle("");
      setDescription("");
      setContentUrl("");
      setFormTitle("");
      setFormSubtitleLine1("");
      setFormSubtitleLine2("");
      setCtaButton1Link("");
      setCtaButton2Link("");
      setCtaButton1Label("");
      setCtaButton2Label("");
      setContentSource("url");
      setContentFile(null);
      setThumbnailFile(null);
      setFormationsOffertes([]);
      setShowFormationsSection(false);
      const randomUuid = Math.random().toString(36).substring(2, 22);
      setUuid(randomUuid);
    }
  }, [editData, isOpen]);

  // Helper functions for formations offertes
  const addFormationEntry = () => {
    if (formationsOffertes.length >= 4) return;
    setFormationsOffertes([...formationsOffertes, {
      id: Date.now().toString(),
      youtubeUrl: "",
      title: "",
      downloads: [],
      downloadFiles: []
    }]);
    setShowFormationsSection(true);
  };

  const removeFormationEntry = (id) => {
    setFormationsOffertes(formationsOffertes.filter(f => f.id !== id));
    if (formationsOffertes.length === 1) {
      setShowFormationsSection(false);
    }
  };

  const updateFormationEntry = (id, field, value) => {
    setFormationsOffertes(formationsOffertes.map(f => 
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const addDownloadToFormation = (formationId, downloadLabel = "") => {
    setFormationsOffertes(formationsOffertes.map(f => 
      f.id === formationId 
        ? { 
            ...f, 
            downloads: [...(f.downloads || []), { id: Date.now().toString(), label: downloadLabel, url: "", file: null }]
          }
        : f
    ));
  };

  const removeDownloadFromFormation = (formationId, downloadId) => {
    setFormationsOffertes(formationsOffertes.map(f => 
      f.id === formationId 
        ? { ...f, downloads: (f.downloads || []).filter(d => d.id !== downloadId) }
        : f
    ));
  };

  const updateDownloadInFormation = (formationId, downloadId, field, value) => {
    setFormationsOffertes(formationsOffertes.map(f => 
      f.id === formationId 
        ? {
            ...f,
            downloads: (f.downloads || []).map(d => 
              d.id === downloadId ? { ...d, [field]: value } : d
            )
          }
        : f
    ));
  };

  const setDownloadFileInFormation = (formationId, downloadId, file) => {
    setFormationsOffertes(formationsOffertes.map(f => 
      f.id === formationId 
        ? {
            ...f,
            downloads: (f.downloads || []).map(d => {
              if (d.id !== downloadId) return d;
              const next = { ...d, file };
              if (file && !d.label?.trim()) next.label = file.name;
              return next;
            })
          }
        : f
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // if (contentSource === "url" && !contentUrl.trim()) return;
    // if (contentSource === "upload" && !contentFile) return;
    const existingContentUrl = (editData?.contentUrl || "").trim();
    const typedContentUrl = contentUrl.trim();

    if (contentSource === "url" && !typedContentUrl) {
      toast.error("Please enter content URL");
      return;
    }
    // - Edit: allow saving without re-upload if an existing URL already exists
    if (contentSource === "upload" && !contentFile && !existingContentUrl) {
      toast.error("Please upload a content file");
      return;
    }
    setSaving(true);
    setUploadProgress(0);
    const leadMagnetUrl = `https://discovery.orra-academy.com/?id=${uuid}`;
    const data = {
      title,
      description,
      uuid,
      contentSource,
      contentUrl: contentSource === "url" ? contentUrl : undefined,
      contentFile: contentSource === "upload" ? contentFile : undefined,
      thumbnailFile: contentSource === "upload" ? thumbnailFile : undefined,
      lead_magnet_url: leadMagnetUrl,
      id: editData?.id || uuid,
      formTitle: formTitle.trim() || undefined,
      formSubtitleLine1: formSubtitleLine1.trim() || undefined,
      formSubtitleLine2: formSubtitleLine2.trim() || undefined,
      ctaButton1Link: ctaButton1Link.trim() || undefined,
      ctaButton2Link: ctaButton2Link.trim() || undefined,
      ctaButton1Label: ctaButton1Label.trim() || undefined,
      ctaButton2Label: ctaButton2Label.trim() || undefined,
      formationsOffertes: formationsOffertes.filter(f => f.youtubeUrl && f.title).length > 0 
        ? formationsOffertes.filter(f => f.youtubeUrl && f.title).map(f => ({
            youtubeUrl: f.youtubeUrl.trim(),
            title: f.title.trim(),
            downloads: f.downloads || [],
            downloadFiles: f.downloadFiles || []
          }))
        : undefined,
    };
    try {
      await onSave(data, { onProgress: setUploadProgress });
      onClose();
    }catch (error){
      console.error("Save error ", error);
      toast.error(error?.message || "Failed to save lead magnet");
    }
     finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0b1739] rounded-lg shadow-lg w-full max-w-xl border border-slate-700 flex flex-col max-h-[90vh]">
        <div className="p-4 border-b border-slate-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-white">
            {editData ? "Edit Lead Magnet" : "Create New Lead Magnet"}
          </h2>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 p-4 space-y-3">
          <div>
            <label
              className="text-start block text-gray-400 text-sm font-bold mb-1"
              htmlFor="title"
            >
              Title:
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-[#081028] text-white border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label
              className="text-start block text-gray-400 text-sm font-bold mb-1"
              htmlFor="description"
            >
              Description:
            </label>
            <div className="lead-magnet-quill">
              <ReactQuill
                value={description}
                onChange={setDescription}
                className="w-full bg-transparent text-white rounded-[15px]"
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                    ["clean"],
                  ],
                }}
                theme="snow"
              />
            </div>
            <style>{`
              .lead-magnet-quill .ql-container {
                background-color: #081028;
                color: white;
                border: 1px solid #475569;
                border-bottom-left-radius: 0.375rem;
                border-bottom-right-radius: 0.375rem;
              }
              .lead-magnet-quill .ql-toolbar {
                background-color: #081028;
                border: 1px solid #475569;
                border-top-left-radius: 0.375rem;
                border-top-right-radius: 0.375rem;
                border-bottom: none;
              }
              .lead-magnet-quill .ql-editor {
                color: white;
                min-height: 100px;
              }
              .lead-magnet-quill .ql-editor.ql-blank::before {
                color: #9ca3af;
              }
              .lead-magnet-quill .ql-stroke {
                stroke: #9ca3af;
              }
              .lead-magnet-quill .ql-fill {
                fill: #9ca3af;
              }
              .lead-magnet-quill .ql-picker-label {
                color: #9ca3af;
              }
              .lead-magnet-quill .ql-toolbar button:hover .ql-stroke,
              .lead-magnet-quill .ql-toolbar button:focus .ql-stroke,
              .lead-magnet-quill .ql-toolbar button.ql-active .ql-stroke {
                stroke: #3b82f6;
              }
              .lead-magnet-quill .ql-toolbar button:hover .ql-fill,
              .lead-magnet-quill .ql-toolbar button:focus .ql-fill,
              .lead-magnet-quill .ql-toolbar button.ql-active .ql-fill {
                fill: #3b82f6;
              }
            `}</style>
          </div>
          <div>
            <label
              className="text-start block text-gray-400 text-sm font-bold mb-1"
              htmlFor="uuid"
            >
              UUID:
            </label>
            <input
              type="text"
              id="uuid"
              value={uuid}
              onChange={(e) => setUuid(e.target.value.substring(0, 20))}
              className="w-full px-3 py-2 bg-[#081028] text-white border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={20}
              required
              disabled={!!editData}
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum 20 characters {editData && "(Cannot edit UUID)"}
            </p>
          </div>
          <div>
            <span className="text-start block text-gray-400 text-sm font-bold mb-2">Content</span>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="contentSource"
                  checked={contentSource === "url"}
                  onChange={() => setContentSource("url")}
                  className="text-blue-600"
                />
                <span className="text-gray-300 text-sm">Content URL</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="contentSource"
                  checked={contentSource === "upload"}
                  onChange={() => setContentSource("upload")}
                  className="text-blue-600"
                />
                <span className="text-gray-300 text-sm">Upload</span>
              </label>
            </div>
            {contentSource === "url" ? (
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="contentUrl">
                  Content URL:
                </label>
                <input
                  type="url"
                  id="contentUrl"
                  value={contentUrl}
                  onChange={(e) => setContentUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/content"
                  required={contentSource === "url"}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="contentFile">
                    Content file:
                  </label>
                  <input
                    type="file"
                    id="contentFile"
                    accept="video/*,audio/*,.pdf,.mp4,.webm,.mov"
                    onChange={(e) => setContentFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 bg-[#081028] text-white border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-600 file:text-white file:text-sm"
                  />
                  {contentFile && <p className="text-xs text-gray-500 mt-1">{contentFile.name}</p>}
                </div>
                <div>
                  <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="thumbnailFile">
                    Thumbnail file:
                  </label>
                  <input
                    type="file"
                    id="thumbnailFile"
                    accept="image/*"
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 bg-[#081028] text-white border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-600 file:text-white file:text-sm"
                  />
                  {thumbnailFile && <p className="text-xs text-gray-500 mt-1">{thumbnailFile.name}</p>}
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-slate-600 pt-3 mt-3">
            <p className="text-gray-400 text-sm font-bold mb-2">Lead form (public page)</p>
            <div className="space-y-3">
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="formTitle">
                  Form title:
                </label>
                <input
                  type="text"
                  id="formTitle"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Récupère le Guide..."
                />
              </div>
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="formSubtitleLine1">
                  Form subtitle (line 1):
                </label>
                <input
                  type="text"
                  id="formSubtitleLine1"
                  value={formSubtitleLine1}
                  onChange={(e) => setFormSubtitleLine1(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Si vous souhaitez..."
                />
              </div>
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="formSubtitleLine2">
                  Form subtitle (line 2):
                </label>
                <input
                  type="text"
                  id="formSubtitleLine2"
                  value={formSubtitleLine2}
                  onChange={(e) => setFormSubtitleLine2(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Découvrez ORRA Academy"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-slate-600 pt-3 mt-3">
            <p className="text-gray-400 text-sm font-bold mb-2">CTA buttons (course sidebar)</p>
            <p className="text-gray-500 text-xs mb-2">If set, sidebar CTA buttons use these links; otherwise they open the booking modal.</p>
            <div className="space-y-3">
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="ctaButton1Link">
                  CTA 1 link:
                </label>
                <input
                  type="url"
                  id="ctaButton1Link"
                  value={ctaButton1Link}
                  onChange={(e) => setCtaButton1Link(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
                <label className="text-start block text-gray-400 text-xs font-bold mb-1 mt-1" htmlFor="ctaButton1Label">
                  CTA 1 label (optional):
                </label>
                <input
                  type="text"
                  id="ctaButton1Label"
                  value={ctaButton1Label}
                  onChange={(e) => setCtaButton1Label(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Accéder à la formation"
                />
              </div>
              <div>
                <label className="text-start block text-gray-400 text-xs font-bold mb-1" htmlFor="ctaButton2Link">
                  CTA 2 link:
                </label>
                <input
                  type="url"
                  id="ctaButton2Link"
                  value={ctaButton2Link}
                  onChange={(e) => setCtaButton2Link(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..."
                />
                <label className="text-start block text-gray-400 text-xs font-bold mb-1 mt-1" htmlFor="ctaButton2Label">
                  CTA 2 label (optional):
                </label>
                <input
                  type="text"
                  id="ctaButton2Label"
                  value={ctaButton2Label}
                  onChange={(e) => setCtaButton2Label(e.target.value)}
                  className="w-full px-3 py-2 bg-[#081028] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Réserver un appel"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-slate-600 pt-3 mt-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-gray-400 text-sm font-bold">FORMATIONS OFFERTES</p>
                <p className="text-gray-500 text-xs mt-1">Add up to 4 videos with optional file downloads</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFormationsSection(!showFormationsSection)}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                {showFormationsSection ? "Hide" : "Show"}
              </button>
            </div>
            {showFormationsSection && (
              <div className="space-y-4">
                {formationsOffertes.map((formation, idx) => (
                  <div key={formation.id} className="bg-[#081028] border border-slate-600 rounded-md p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-gray-300 text-sm font-semibold">Video {idx + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeFormationEntry(formation.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                    <div>
                      <label className="text-start block text-gray-400 text-xs font-bold mb-1">
                        YouTube Video URL <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="url"
                        value={formation.youtubeUrl || ""}
                        onChange={(e) => updateFormationEntry(formation.id, "youtubeUrl", e.target.value)}
                        className="w-full px-3 py-2 bg-[#0b1739] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://www.youtube.com/watch?v=..."
                        required
                      />
                    </div>
                    <div>
                      <label className="text-start block text-gray-400 text-xs font-bold mb-1">
                        Video Title <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={formation.title || ""}
                        onChange={(e) => updateFormationEntry(formation.id, "title", e.target.value)}
                        className="w-full px-3 py-2 bg-[#0b1739] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. Introduction to AI"
                        required
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-start block text-gray-400 text-xs font-bold">
                          Download Files (Optional)
                        </label>
                        <button
                          type="button"
                          onClick={() => addDownloadToFormation(formation.id)}
                          className="text-blue-400 hover:text-blue-300 text-xs font-medium"
                        >
                          + Add File
                        </button>
                      </div>
                      {formation.downloads && formation.downloads.length > 0 && (
                        <div className="space-y-2">
                          {formation.downloads.map((download) => (
                            <div key={download.id} className="flex gap-2 items-start">
                              <div className="flex-1 space-y-2">
                                <input
                                  type="text"
                                  value={download.label || ""}
                                  onChange={(e) => updateDownloadInFormation(formation.id, download.id, "label", e.target.value)}
                                  className="w-full px-2 py-1.5 bg-[#0b1739] text-white placeholder:text-gray-500 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                                  placeholder="File label (e.g. PDF Guide)"
                                />
                                {download.url && !download.file && (
                                  <div className="px-2 py-1.5 bg-[#0b1739] border border-slate-600 rounded-md">
                                    <p className="text-xs text-gray-400 mb-1">Current file:</p>
                                    <a 
                                      href={download.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-400 hover:text-blue-300 break-all"
                                    >
                                      {download.url}
                                    </a>
                                    <p className="text-xs text-gray-500 mt-1">Upload a new file to replace</p>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  onChange={(e) => setDownloadFileInFormation(formation.id, download.id, e.target.files?.[0] || null)}
                                  className="w-full px-2 py-1.5 bg-[#0b1739] text-white border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 file:mr-2 file:py-0.5 file:px-2 file:rounded file:border-0 file:bg-slate-600 file:text-white file:text-xs text-xs"
                                  accept=".pdf,.zip,.docx,.doc,.txt"
                                />
                                {download.file && (
                                  <p className="text-xs text-gray-500">{download.file.name}</p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeDownloadFromFormation(formation.id, download.id)}
                                className="text-red-400 hover:text-red-300 text-sm mt-1"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {formationsOffertes.length < 4 && (
                  <button
                    type="button"
                    onClick={addFormationEntry}
                    className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors text-sm font-medium"
                  >
                    + Add Video ({formationsOffertes.length}/4)
                  </button>
                )}
                {formationsOffertes.length === 0 && (
                  <button
                    type="button"
                    onClick={addFormationEntry}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm font-medium"
                  >
                    + Add First Video
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="rounded-md">
            <label className="text-start block text-gray-400 text-xs font-bold mb-1">
              Generated Lead Magnet URL:
            </label>
            <p className="text-start text-blue-400 text-xs break-all bg-[#373e53] p-3 rounded-md border border-slate-600">
              https://discovery.orra-academy.com/?id={uuid || "..."}
            </p>
          </div>
        </div>
        <div className="p-4 border-t border-slate-700 flex justify-end items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[140px] justify-center"
          >
            {saving ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {contentSource === "upload"
                  ? `Uploading... ${uploadProgress}%`
                  : "Saving..."}
              </>
            ) : (
              editData ? "Update Lead Magnet" : "Create Lead Magnet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


export default LeadMagnetModal;